'use client';

import { ReviewProductForm } from '@components/ReviewProductDialog/ReviewProductDialog';
import type { AuthState } from '@context/authContext';
import { useAuth } from '@context/authContext';
import { Button } from '@ngg/components';
import { StarIcon } from '@ngg/icons';
import { addProductReviews } from '@server/requests/reviews';
import { sortReviews } from '@server/requests/reviews/sortReviews';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'next/navigation';
import type { FormEvent } from 'react';
import { useEffect, useRef, useState } from 'react';
import type { Dictionary } from 'types/common';
import type {
    AddReivewPayload,
    TReviewsData,
    YourReview,
} from 'types/testFreaks';

import type { MarketConfiguration } from '@/config/markets/const';

const getFormFields = (e: FormEvent<HTMLFormElement>) => {
    const formData = new FormData(e.currentTarget);
    const formProps = Object.fromEntries(formData);

    return formProps;
};

function Review({
    review,
    dictionary,
}: {
    review: YourReview;
    dictionary: Dictionary;
}) {
    const debounce = useRef<ReturnType<typeof setTimeout>>();
    const { data } = useQuery<AuthState>({ queryKey: ['auth'] });

    const [vote, setVote] = useState(review.votes_up - review.votes_down);
    const upVoteReview = useMutation({
        mutationFn: () =>
            fetch(
                `${review.feedback_url}?&type=up&voter_id=${data?.user?.uid}`,
                {
                    method: 'POST',
                },
            ),
        onMutate: () => setVote(vote + 1),
        onError: () => setVote(review.votes_up - review.votes_down),
    });

    return (
        <div className="border-b border-grey-150 py-4 text-sm last-of-type:border-none">
            <div className="flex gap-3">
                <p className="font-medium">{review.author}</p>
                <span>|</span>
                <p>{review.date}</p>
            </div>
            <div className="mt-2 flex text-base">
                {[...Array(5)].map((_, i) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <StarIcon key={i} filled={i < review.score} />
                ))}
            </div>
            <div className="my-4">
                <p>{review.extract}</p>
            </div>
            <button
                type="button"
                disabled={!data?.user}
                className="mt-2 text-xs text-grey-300 underline disabled:no-underline"
                onClick={async () => {
                    await new Promise<void>((resolve) => {
                        if (debounce.current) clearTimeout(debounce.current);
                        else upVoteReview.mutate();
                        debounce.current = setTimeout(resolve, 500);
                    }).then(() => {
                        debounce.current = undefined;
                    });
                }}>
                {dictionary?.likeReview} ({vote})
            </button>
        </div>
    );
}

export default function ReviewContent({
    initialReviews,
    dictionary,
    productId,
    marketConfig,
}: {
    initialReviews: TReviewsData | null;
    dictionary: Dictionary;
    productId: string;
    marketConfig: MarketConfiguration;
}) {
    const { data } = useQuery<AuthState>({ queryKey: ['auth'] });
    const searchParams = useSearchParams();
    const [variantId, setVariantId] = useState<string>('');
    const { showLogin } = useAuth();
    const [noOfReviewsToShow, setNoOfReviewsToShow] = useState(5);
    const [reviews, setReviews] = useState<YourReview[]>(
        initialReviews?.your_review_answers?.reviews || [],
    );

    useEffect(() => {
        setVariantId(searchParams.get('variant') || '');
    }, [searchParams]);

    const [loading, setLoading] = useState(false);
    const [isReviewFormOpen, setIsReviewFormOpen] = useState(false);
    const isDisabled = noOfReviewsToShow >= reviews.length;

    const getMoreReviews = async () => {
        setNoOfReviewsToShow(noOfReviewsToShow + 5);
    };

    const onSubmitReview = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setLoading(true);
        const payload = getFormFields(e) as AddReivewPayload;

        const res = await addProductReviews({
            productId,
            variantId,
            market: marketConfig.code,
            ...payload,
        });

        if (!res?.error && res?.data?.your_review_answers?.reviews?.length) {
            const updatedReviews = res?.data.your_review_answers?.reviews;
            sortReviews(updatedReviews);
            setReviews(updatedReviews);
        }

        setIsReviewFormOpen(false);
        setLoading(false);
    };

    return (
        <>
            {isReviewFormOpen ? (
                <div className="border-b border-grey-150 py-8">
                    <ReviewProductForm
                        onSubmit={onSubmitReview}
                        dictionary={dictionary}
                        classNames={{ wrapper: 'md:p-0' }}
                        onClose={() => setIsReviewFormOpen(false)}
                        isLoading={loading}
                    />
                </div>
            ) : null}
            <div className="flex items-center justify-between pb-3">
                {!isReviewFormOpen ? (
                    <Button
                        type="button"
                        className="btn-primary"
                        onClick={() => {
                            if (!data?.user) {
                                showLogin(false);
                                return;
                            }
                            setIsReviewFormOpen(!isReviewFormOpen);
                        }}>
                        <p className="text-sm">{dictionary?.leaveAReview}</p>
                    </Button>
                ) : null}
            </div>

            {reviews?.length ? (
                <div>
                    {reviews
                        ?.slice(0, noOfReviewsToShow)
                        .map((review) => (
                            <Review
                                key={review.author + review.date}
                                review={review}
                                dictionary={dictionary}
                            />
                        ))}
                    {isDisabled ? null : (
                        <Button
                            fullWidth
                            isLoading={loading}
                            disabled={isDisabled}
                            type="button"
                            className="btn-outlined"
                            onClick={() => getMoreReviews()}>
                            {dictionary?.showMoreReviews}
                        </Button>
                    )}
                </div>
            ) : (
                <p className="text-sm">{dictionary?.noReviews}</p>
            )}
        </>
    );
}
